import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon, NGRPLogoMark } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { NGRP_MARATHI_BANNER_URL, NGRP_LOGO_URL, FOUNDER_SQUARE_URL } from '@/lib/config';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ShieldCheck, Users2, Cpu, Building2, ScrollText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function AboutPage() {
  const { t } = useLang();
  const principleIcons = [ShieldCheck, ScrollText, Users2, Cpu, Building2];
  return (
    <div>
      <section className="hero-surface">
        <Container className="py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <SectionKicker>{t('nav.about')}</SectionKicker>
              <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
                {t('about.title')}
              </h1>
              <p className="mt-4 max-w-3xl text-[#5B6B82] text-base leading-relaxed">{t('about.lede')}</p>
            </div>
            <div className="lg:col-span-4 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 rounded-full bg-gradient-to-br from-[#F47B20]/10 via-white to-[#138A4B]/10 blur-2xl" aria-hidden="true" />
                <img
                  src={NGRP_LOGO_URL}
                  alt="NGRP logo"
                  width={256}
                  height={256}
                  loading="eager"
                  decoding="async"
                  className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-full object-contain bg-white p-2 border border-[#D7E1EF] shadow-md"
                />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-14 sm:py-16">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 space-y-8">
              <Block heading={t('about.vision_h')} body={t('about.vision_p')} />
              <Block heading={t('about.mission_h')} body={t('about.mission_p')} />
              <Block heading={t('about.why_h')} body={t('about.why_p')} />
              <Block heading={t('about.leadership_h')} body={t('about.leadership_p')} />
              <Block heading={t('about.constitution_h')} body={t('about.constitution_p')} />

              {/* Emblem preview */}
              <Card className="border-[#D7E1EF] bg-white" data-testid="about-emblem-preview">
                <CardContent className="p-5 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-5 items-center">
                    <div className="sm:col-span-3 flex sm:justify-center">
                      <img
                        src={NGRP_LOGO_URL}
                        alt="NGRP Official Party Emblem"
                        width={128}
                        height={128}
                        loading="lazy"
                        decoding="async"
                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-contain border border-[#D7E1EF] bg-white p-1"
                      />
                    </div>
                    <div className="sm:col-span-9">
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">
                        NGRP Logo — Meaning &amp; Vision
                      </div>
                      <div className="mt-1 font-display text-lg font-semibold text-[#0B1220]">
                        Governance philosophy, in visual form.
                      </div>
                      <p className="mt-2 text-sm text-[#5B6B82] leading-relaxed">
                        Every element of the NGRP emblem — the rising sun, the gear, the developed-India skyline, the people at the centre, the open book, the Ashoka Chakra and the green laurel — carries a specific meaning. Together they express a new dawn of transparent, accountable and citizen-centric governance for a stronger India.
                      </p>
                      <div className="mt-4">
                        <Link to="/emblem">
                          <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="about-emblem-cta">
                            Read the full emblem meaning
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Founder preview */}
              <Card className="border-[#D7E1EF] bg-white" data-testid="about-founder-preview">
                <CardContent className="p-5 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-5 items-center">
                    <div className="sm:col-span-3 flex sm:justify-center">
                      <div
                        className="h-24 w-24 sm:h-28 sm:w-28 rounded-full overflow-hidden border border-[#D7E1EF] bg-[#F5F8FC] shadow-md"
                        data-testid="about-founder-photo-frame"
                      >
                        <img
                          src={FOUNDER_SQUARE_URL}
                          alt="Ajay Pachpute, Founder of NGRP"
                          width={112}
                          height={112}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-9">
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">
                        About the Founder
                      </div>
                      <div className="mt-1 font-display text-lg font-semibold text-[#0B1220]">
                        Ajay Pachpute — Founder, NGRP
                      </div>
                      <p className="mt-2 text-sm text-[#5B6B82] leading-relaxed">
                        Civil engineer, entrepreneur and infrastructure professional whose experience across government contracting, infrastructure and renewable energy shaped a conviction that India needs continuous system-reform — not just changes in leadership.
                      </p>
                      <div className="mt-4">
                        <Link to="/founder">
                          <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="about-founder-cta">
                            Read the founder’s story
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <aside className="lg:col-span-5 space-y-6">
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-4">
                  <AspectRatio ratio={3 / 2} className="overflow-hidden rounded-lg border border-[#D7E1EF]">
                    <img
                      src={NGRP_MARATHI_BANNER_URL}
                      alt="NGRP values banner (Marathi)"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                      data-testid="about-marathi-banner"
                    />
                  </AspectRatio>
                  <p className="mt-3 text-xs text-[#5B6B82] leading-relaxed">{t('about.banner_caption')}</p>
                </CardContent>
              </Card>
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold text-[#062B5C]">{t('about.principles_h')}</div>
                  <ul className="mt-4 space-y-3">
                    {[
                      t('about.principle_1'),
                      t('about.principle_2'),
                      t('about.principle_3'),
                      t('about.principle_4'),
                      t('about.principle_5'),
                    ].map((p, i) => {
                      const Icon = principleIcons[i] || ShieldCheck;
                      return (
                        <li key={i} className="flex items-start gap-3">
                          <span className="h-7 w-7 rounded-md bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="text-sm text-[#0B1220] leading-relaxed">{p}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-5">
                    <Link to="/vision">
                      <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="about-read-vision">
                        {t('nav.vision')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function Block({ heading, body }) {
  return (
    <div>
      <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#0B1220]">{heading}</h2>
      <p className="mt-3 text-[15px] text-[#5B6B82] leading-relaxed">{body}</p>
    </div>
  );
}
